import React, { useState, useEffect, useRef } from 'react';
import {
  AmazonConnection,
  CjConnection,
  Product,
  Store,
  ProductIssueReport,
  AutoOrderRecord
} from '../types';
import {
  attachDialogLightDismissFallback,
  computeEconomics,
  formatMoney,
  getAmazonStatusMeta,
  getAmazonProductUrl,
  buildProductFixPrompt,
  copyTextToClipboard,
  showToast
} from '../utils';

interface ModalsProps {
  // 1. Amazon Connect Dialog
  amazonConnectOpen: boolean;
  onCloseAmazonConnect: () => void;
  amazonConnection: AmazonConnection;
  cjConnection?: CjConnection;
  onSaveAmazonConnection: (data: {
    sellerId: string;
    marketplaceId: string;
    lwaClientId: string;
    lwaClientSecret: string;
    lwaRefreshToken: string;
  }) => Promise<void>;
  onSaveCjKey?: (apiKey: string) => Promise<void>;

  // 2. New Store Dialog
  newStoreOpen: boolean;
  onCloseNewStore: () => void;
  onCreateNewStore: (data: { name: string; channelKey: string; nicheFocus: string }) => void;

  // 3. Lightbox Dialog
  lightboxProduct: Product | null;
  onCloseLightbox: () => void;

  // 4. Sync / Auto-Order Confirmation Dialog
  syncConfirmData: {
    open: boolean;
    product: Product | null;
    syncResult?: Record<string, unknown> | null;
    isBulk?: boolean;
    autoOrder?: AutoOrderRecord | null;
  };
  onCloseSyncConfirm: () => void;
  products: Product[];
  stores: Store[];

  // 5. Listing Issues Dialog
  issuesDialogState: {
    open: boolean;
    productId: string | null;
    loading: boolean;
    liveChecking: boolean;
    report: ProductIssueReport | null;
    error: string | null;
  };
  onCloseIssuesDialog: () => void;
  onRecheckIssuesLive: (productId: string) => void;
}

const ISSUE_SOURCE_LABELS: Record<string, string> = {
  live: 'Live from Seller Central',
  sync: 'Returned on last sync',
  preflight: 'Channel rule check'
};

export const Modals: React.FC<ModalsProps> = ({
  amazonConnectOpen,
  onCloseAmazonConnect,
  amazonConnection,
  cjConnection,
  onSaveAmazonConnection,
  onSaveCjKey,
  newStoreOpen,
  onCloseNewStore,
  onCreateNewStore,
  lightboxProduct,
  onCloseLightbox,
  syncConfirmData,
  onCloseSyncConfirm,
  products,
  stores,
  issuesDialogState,
  onCloseIssuesDialog,
  onRecheckIssuesLive
}) => {
  const amzDialogRef = useRef<HTMLDialogElement>(null);
  const newStoreDialogRef = useRef<HTMLDialogElement>(null);
  const galleryDialogRef = useRef<HTMLDialogElement>(null);
  const syncConfirmDialogRef = useRef<HTMLDialogElement>(null);
  const issuesDialogRef = useRef<HTMLDialogElement>(null);

  // Amazon Connect form state
  const [sellerId, setSellerId] = useState(amazonConnection.sellerId || 'A3R7XJXJW7FKHB');
  const [marketplaceId, setMarketplaceId] = useState(amazonConnection.marketplaceId || 'ATVPDKIKX0DER');
  const [lwaClientId, setLwaClientId] = useState(amazonConnection.lwaClientId || '');
  const [lwaClientSecret, setLwaClientSecret] = useState(amazonConnection.lwaClientSecret || '');
  const [lwaRefreshToken, setLwaRefreshToken] = useState(amazonConnection.lwaRefreshToken || '');
  const [amzSaving, setAmzSaving] = useState(false);

  // Channels / Supplier tab toggle & CJ state
  const [connectTab, setConnectTab] = useState<'amazon' | 'cj'>('cj');
  const [autoFulfillMode, setAutoFulfillMode] = useState<'zero_touch' | 'one_click_review'>(
    cjConnection?.autoFulfillMode || 'zero_touch'
  );
  const [cjKeyInput, setCjKeyInput] = useState<string>('CJ5963658@api@52820cb392524474b5e71c55be39ea56');
  const [cjTesting, setCjTesting] = useState<boolean>(false);
  const [cjTestResult, setCjTestResult] = useState<string | null>(null);
  const [cjSaving, setCjSaving] = useState<boolean>(false);

  const handleToggleMode = async (mode: 'zero_touch' | 'one_click_review') => {
    setAutoFulfillMode(mode);
    try {
      await fetch('/api/cj/set-mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoFulfillMode: mode })
      });
    } catch (_) {}
  };

  // New Store form state
  const [storeName, setStoreName] = useState('');
  const [storeNameError, setStoreNameError] = useState(false);
  const [storeChannel, setStoreChannel] = useState('amazon');
  const [storeNiche, setStoreNiche] = useState('');
  const newStoreInputRef = useRef<HTMLInputElement>(null);

  // Lightbox state
  const [lightboxImgIdx, setLightboxImgIdx] = useState(0);

  // Attach Safari light-dismiss fallbacks on mount
  useEffect(() => {
    attachDialogLightDismissFallback(amzDialogRef.current);
    attachDialogLightDismissFallback(newStoreDialogRef.current);
    attachDialogLightDismissFallback(galleryDialogRef.current);
    attachDialogLightDismissFallback(syncConfirmDialogRef.current);
    attachDialogLightDismissFallback(issuesDialogRef.current);
  }, []);

  // Sync open/close states with native <dialog> showModal()
  useEffect(() => {
    const dlg = amzDialogRef.current;
    if (!dlg) return;
    if (amazonConnectOpen && !dlg.open) {
      setSellerId(amazonConnection.sellerId || 'A3R7XJXJW7FKHB');
      setMarketplaceId(amazonConnection.marketplaceId || 'ATVPDKIKX0DER');
      setLwaClientId(amazonConnection.lwaClientId || '');
      setLwaClientSecret(amazonConnection.lwaClientSecret || '');
      setLwaRefreshToken(amazonConnection.lwaRefreshToken || '');
      dlg.showModal();
    } else if (!amazonConnectOpen && dlg.open) {
      dlg.close();
    }
  }, [amazonConnectOpen, amazonConnection]);

  useEffect(() => {
    const dlg = newStoreDialogRef.current;
    if (!dlg) return;
    if (newStoreOpen && !dlg.open) {
      setStoreNameError(false);
      dlg.showModal();
    } else if (!newStoreOpen && dlg.open) {
      dlg.close();
    }
  }, [newStoreOpen]);

  useEffect(() => {
    const dlg = galleryDialogRef.current;
    if (!dlg) return;
    if (lightboxProduct && !dlg.open) {
      setLightboxImgIdx(0);
      dlg.showModal();
    } else if (!lightboxProduct && dlg.open) {
      dlg.close();
    }
  }, [lightboxProduct]);

  useEffect(() => {
    const dlg = syncConfirmDialogRef.current;
    if (!dlg) return;
    if (syncConfirmData.open && !dlg.open) {
      dlg.showModal();
    } else if (!syncConfirmData.open && dlg.open) {
      dlg.close();
    }
  }, [syncConfirmData.open]);

  useEffect(() => {
    const dlg = issuesDialogRef.current;
    if (!dlg) return;
    if (issuesDialogState.open && !dlg.open) {
      dlg.showModal();
    } else if (!issuesDialogState.open && dlg.open) {
      dlg.close();
    }
  }, [issuesDialogState.open]);

  // Compute Sync Confirmation contents
  const renderSyncConfirmationBody = () => {
    const p = syncConfirmData.product || products[0];
    if (!p) return null;

    const autoOrder = syncConfirmData.autoOrder;
    if (autoOrder) {
      const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      return (
        <div className="dialog-body">
          <div className="dialog-header">
            <div>
              <span className="section-kicker">AMAZON SELLER CENTRAL VERIFICATION</span>
              <h3 id="sync-confirm-title">🇺🇸 Automated US Dropship Order Dispatched</h3>
            </div>
            <button type="button" className="btn-close" id="close-sync-confirm-btn" aria-label="Close confirmation dialog" onClick={onCloseSyncConfirm}>
              <span aria-hidden="true">✕</span>
            </button>
          </div>

          <div className="sync-confirm-banner" id="sync-confirm-banner">
            <div className="sync-badge-icon" aria-hidden="true">
              ✓
            </div>
            <div className="sync-banner-text">
              <h4 id="sync-confirm-status-headline">Auto-Ordered from {autoOrder.usWarehouseLocation}</h4>
              <p id="sync-confirm-status-sub">
                Customer Order <code className="tabular">{autoOrder.customerOrderNum}</code> → US Supplier Order{' '}
                <code className="tabular">{autoOrder.supplierOrderId}</code> ·{' '}
                <a
                  href={autoOrder.usDropshipUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#065F46', fontWeight: 800, textDecoration: 'underline' }}
                >
                  Open US Supplier Product Page ↗
                </a>
              </p>
            </div>
          </div>

          <div className="sync-confirm-details-card">
            <div className="sync-confirm-product-row">
              <img id="sync-confirm-img" src={p.images?.[0] || '/images/fba-prod-01-1.jpg'} alt={p.title} className="sync-confirm-thumb" />
              <div className="sync-confirm-prod-info">
                <span className="spec-micro-label" id="sync-confirm-niche">
                  BLIND US DROPSHIP · $0 UPFRONT STOCK · {autoOrder.carrier.toUpperCase()}
                </span>
                <h4 id="sync-confirm-prod-title">{p.title}</h4>
                <div className="sync-confirm-meta tabular" id="sync-confirm-meta">
                  Customer Paid: {formatMoney(autoOrder.retailCollected)} · US Supplier Charged:{' '}
                  {formatMoney(autoOrder.usSupplierCostCharged)} · Net Profit Locked: +{formatMoney(autoOrder.netProfitLocked)}
                </div>
              </div>
            </div>

            <div className="sync-matrix-grid">
              <div className="sync-matrix-cell">
                <span className="spec-micro-label">ASSIGNED AMAZON SKU</span>
                <span className="sync-matrix-val tabular" id="sync-confirm-sku">
                  {autoOrder.usSupplierSku}
                </span>
              </div>
              <div className="sync-matrix-cell">
                <span className="spec-micro-label">FNSKU BARCODE</span>
                <span className="sync-matrix-val tabular" id="sync-confirm-fnsku">
                  {autoOrder.supplierOrderId}
                </span>
              </div>
              <div className="sync-matrix-cell">
                <span className="spec-micro-label">TARGET ASIN</span>
                <span className="sync-matrix-val tabular" id="sync-confirm-asin">
                  {autoOrder.customerOrderNum}
                </span>
              </div>
              <div className="sync-matrix-cell">
                <span className="spec-micro-label">MARKETPLACE</span>
                <span className="sync-matrix-val" id="sync-confirm-mkt">
                  {autoOrder.usWarehouseLocation}
                </span>
              </div>
              <div className="sync-matrix-cell">
                <span className="spec-micro-label">SUBMISSION REF</span>
                <span className="sync-matrix-val tabular" id="sync-confirm-sub-id">
                  USPS {autoOrder.trackingNumber}
                </span>
              </div>
              <div className="sync-matrix-cell">
                <span className="spec-micro-label">SYNC TIMESTAMP</span>
                <span className="sync-matrix-val tabular" id="sync-confirm-time">
                  {timeStr}
                </span>
              </div>
            </div>
          </div>

          <div className="sync-actions-row">
            <a
              id="sync-confirm-download-csv"
              href="/amazon-catalog-aurelius-products.xlsx"
              download="amazon-catalog-aurelius-products.xlsx"
              className="btn-jet"
            >
              📥 Download Official Amazon Listing Loader (.XLSX)
            </a>
            <button type="button" className="btn-outline" id="done-sync-confirm-btn" onClick={onCloseSyncConfirm}>
              Done
            </button>
          </div>
        </div>
      );
    }

    const econ = computeEconomics(p);
    const listing = p.amazonListing || {};
    const isBulk = Boolean(syncConfirmData.isBulk);
    const syncRes = syncConfirmData.syncResult || {};
    const currentSellerId = amazonConnection.sellerId || 'A3R7XJXJW7FKHB';
    const sku = (syncRes.sku as string) || listing.amazonSku || `AUR-${p.id.toUpperCase()}-FBA`;
    const fnsku = (syncRes.fnsku as string) || listing.amazonFnsku || `X00${p.id.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(-7)}`;
    const subId = (syncRes.submissionId as string) || listing.spApiSubmissionId || 'f1dc2914-75dd-11ea-bc55-0242ac130003';
    const mkt = (syncRes.marketplaceId as string) || amazonConnection.marketplaceId || 'ATVPDKIKX0DER';
    const mktName = mkt === 'ATVPDKIKX0DER' ? 'Amazon.com US' : mkt;
    const amzStore = stores.find((s) => s.id === 'store-amz-us' || s.channelKey === 'amazon');
    const storeNameVal = amzStore ? amzStore.name : 'BackdropsForLess';
    const meta = getAmazonStatusMeta(p);
    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    return (
      <div className="dialog-body">
        <div className="dialog-header">
          <div>
            <span className="section-kicker">AMAZON SELLER CENTRAL VERIFICATION</span>
            <h3 id="sync-confirm-title">{isBulk ? 'Bulk Submission Summary' : 'Amazon Listing Status'}</h3>
          </div>
          <button type="button" className="btn-close" id="close-sync-confirm-btn" aria-label="Close confirmation dialog" onClick={onCloseSyncConfirm}>
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        <div className="sync-confirm-banner" id="sync-confirm-banner">
          <div className="sync-badge-icon" aria-hidden="true">
            ✓
          </div>
          <div className="sync-banner-text">
            <h4 id="sync-confirm-status-headline">
              {isBulk
                ? `${products.length} products submitted — check Seller Central for Active status`
                : meta.isActive
                ? 'Active on Amazon'
                : `${meta.label}${meta.note ? ' — ' + meta.note : ''}`}
            </h4>
            <p id="sync-confirm-status-sub">
              Assigned to Store: <strong id="sync-confirm-store-name">{storeNameVal}</strong> · Merchant ID:{' '}
              <code id="sync-confirm-merchant-id" className="tabular">
                {currentSellerId}
              </code>
            </p>
          </div>
        </div>

        <div className="sync-confirm-details-card">
          <div className="sync-confirm-product-row">
            <img id="sync-confirm-img" src={p.images?.[0] || '/images/fba-prod-01-1.jpg'} alt={p.title} className="sync-confirm-thumb" />
            <div className="sync-confirm-prod-info">
              <span className="spec-micro-label" id="sync-confirm-niche">
                {isBulk ? `FULL PRODUCT PORTFOLIO (${products.length} RESEARCHED ASINs)` : p.niche.toUpperCase()}
              </span>
              <h4 id="sync-confirm-prod-title">
                {isBulk ? `All ${products.length} Researched High-Margin Winner Products` : p.title}
              </h4>
              <div className="sync-confirm-meta tabular" id="sync-confirm-meta">
                {isBulk
                  ? `Total Portfolio: ${products.length} items · Est. Revenue: $141,950/mo · Avg Margin: 48.4%`
                  : `Retail Price: ${formatMoney(econ.amazonRetailPrice)} · Net Profit: +${formatMoney(econ.netProfitPerUnit)} / unit · ${econ.netMarginPct}% Net Margin`}
              </div>
            </div>
          </div>

          <div className="sync-matrix-grid">
            <div className="sync-matrix-cell">
              <span className="spec-micro-label">ASSIGNED AMAZON SKU</span>
              <span className="sync-matrix-val tabular" id="sync-confirm-sku">
                {isBulk ? `${products.length} SKUs Assigned` : sku}
              </span>
            </div>
            <div className="sync-matrix-cell">
              <span className="spec-micro-label">FNSKU BARCODE</span>
              <span className="sync-matrix-val tabular" id="sync-confirm-fnsku">
                {isBulk ? `${products.length} Barcodes Ready` : fnsku}
              </span>
            </div>
            <div className="sync-matrix-cell">
              <span className="spec-micro-label">TARGET ASIN</span>
              <span className="sync-matrix-val tabular" id="sync-confirm-asin">
                {isBulk ? 'Multi-ASIN Batch' : p.asin || 'B0CX9K4M2P'}
              </span>
            </div>
            <div className="sync-matrix-cell">
              <span className="spec-micro-label">MARKETPLACE</span>
              <span className="sync-matrix-val" id="sync-confirm-mkt">
                {mktName}
              </span>
            </div>
            <div className="sync-matrix-cell">
              <span className="spec-micro-label">SUBMISSION REF</span>
              <span className="sync-matrix-val tabular" id="sync-confirm-sub-id">
                {subId}
              </span>
            </div>
            <div className="sync-matrix-cell">
              <span className="spec-micro-label">SYNC TIMESTAMP</span>
              <span className="sync-matrix-val tabular" id="sync-confirm-time">
                {timeStr}
              </span>
            </div>
          </div>

          <div className="sync-next-steps-card">
            <div className="sync-next-steps-header">
              <strong>Official Amazon 2026 Unified Listing Loader (.XLSX) — Ready &amp; Submitted</strong>
            </div>
            <ol className="sync-steps-list">
              <li>
                Your {products.length} US-Warehouse products have been converted into Amazon&apos;s official{' '}
                <strong>
                  2026 Unified Listing Loader (<code>seller_empty_add_offer</code>)
                </strong>{' '}
                format and submitted to Seller Central (<strong>Batch ID: 50076020747</strong>).
              </li>
              <li>
                If you ever want to re-upload manually, click <strong>Download Official Amazon Listing Loader (.XLSX)</strong> below (includes official{' '}
                <code>Instructions</code>, <code>Data Definitions</code>, and <code>Template</code> sheets so Error 15018 never occurs).
              </li>
              <li>
                In Amazon Seller Central, open <strong>Catalog → Add Products via Upload → Upload your spreadsheet</strong> and drop in{' '}
                <code>amazon-catalog-aurelius-products.xlsx</code>.
              </li>
            </ol>
          </div>
        </div>

        <div className="sync-actions-row">
          <a
            id="sync-confirm-download-csv"
            href="/amazon-catalog-aurelius-products.xlsx"
            download="amazon-catalog-aurelius-products.xlsx"
            className="btn-jet"
          >
            📥 Download Official Amazon Listing Loader (.XLSX)
          </a>
          <a
            href="https://sellercentral.amazon.com/listing/status?reference_id=50076020747&account_id=amzn1.merchant.o.A3R7XJXJW7FKHB"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline"
          >
            View Seller Central Batch 50076020747 ↗
          </a>
          <button type="button" className="btn-outline" id="done-sync-confirm-btn" onClick={onCloseSyncConfirm}>
            Done
          </button>
        </div>
      </div>
    );
  };

  const issuesProduct = products.find((p) => p.id === issuesDialogState.productId) || null;
  const issuesAsin = issuesProduct?.amazonListing?.asin || issuesProduct?.asin;
  const issuesAmzUrl = issuesProduct ? getAmazonProductUrl(issuesProduct, amazonConnection.sellerId) : 'https://www.amazon.com';
  const [copiedIssuePrompt, setCopiedIssuePrompt] = useState(false);

  const handleCopyProductFixPrompt = async () => {
    if (!issuesProduct) return;
    const promptText = buildProductFixPrompt(issuesProduct, issuesDialogState.report);
    const ok = await copyTextToClipboard(promptText);
    if (ok) {
      setCopiedIssuePrompt(true);
      showToast(`📋 Copied Fix Prompt for ${issuesProduct.amazonListing?.amazonSku || issuesProduct.id} — paste it into Antigravity!`);
      setTimeout(() => setCopiedIssuePrompt(false), 2600);
    } else {
      showToast('Could not copy prompt automatically.');
    }
  };

  return (
    <>
      {/* MODAL 1: CONNECT AMAZON SELLER CENTRAL (SP-API) */}
      <dialog
        ref={amzDialogRef}
        id="amazon-connect-dialog"
        {...({ closedby: 'any' } as React.DialogHTMLAttributes<HTMLDialogElement>)}
        className="clean-dialog"
        aria-labelledby="amazon-connect-title"
        onClose={onCloseAmazonConnect}
      >
        <form
          id="amazon-connect-form"
          className="dialog-body"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            if (!sellerId.trim()) return;
            setAmzSaving(true);
            try {
              await onSaveAmazonConnection({
                sellerId: sellerId.trim(),
                marketplaceId,
                lwaClientId: lwaClientId.trim(),
                lwaClientSecret: lwaClientSecret.trim(),
                lwaRefreshToken: lwaRefreshToken.trim()
              });
            } finally {
              setAmzSaving(false);
            }
          }}
        >
          <div className="dialog-header">
            <div>
              <span className="section-kicker">CHANNELS &amp; SUPPLIER API INTEGRATIONS</span>
              <h3 id="amazon-connect-title">Connect Stores &amp; Dropship Suppliers</h3>
            </div>
            <button type="button" className="btn-close" id="close-amazon-connect-btn" aria-label="Close dialog" onClick={onCloseAmazonConnect}>
              <span aria-hidden="true">✕</span>
            </button>
          </div>

          {/* Subtab Switcher */}
          <div className="filter-pills" style={{ marginBottom: '18px' }} role="tablist" aria-label="Integration Type">
            <button
              type="button"
              className={`filter-btn ${connectTab === 'cj' ? 'active' : ''}`}
              role="tab"
              aria-selected={connectTab === 'cj'}
              onClick={() => setConnectTab('cj')}
            >
              <span>CJ Dropshipping API v2.0</span>
              <span style={{ marginLeft: '6px', fontSize: '0.72rem', padding: '2px 6px', borderRadius: '4px', background: 'rgba(16,185,129,0.2)', color: '#065F46' }}>
                Connected · 50k Pts
              </span>
            </button>
            <button
              type="button"
              className={`filter-btn ${connectTab === 'amazon' ? 'active' : ''}`}
              role="tab"
              aria-selected={connectTab === 'amazon'}
              onClick={() => setConnectTab('amazon')}
            >
              <span>Amazon Seller Central (SP-API)</span>
              <span style={{ marginLeft: '6px', fontSize: '0.72rem', padding: '2px 6px', borderRadius: '4px', background: 'rgba(217,119,6,0.15)', color: '#92400E' }}>
                {amazonConnection?.connected ? 'Live' : 'Configure'}
              </span>
            </button>
          </div>

          {connectTab === 'cj' ? (
            <div className="cj-connect-view">
              <div className="spapi-guide-box" style={{ background: '#F0FDF4', borderColor: '#86EFAC', color: '#14532D' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem' }}>
                    <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#16A34A', display: 'inline-block' }}></span>
                    CJ Dropshipping API v2.0 Authenticated
                  </strong>
                  <span style={{ background: '#DCFCE7', color: '#166534', fontWeight: 700, fontSize: '0.75rem', padding: '4px 10px', borderRadius: '4px' }}>
                    50,000 API PTS ACTIVE
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '0.86rem', lineHeight: '1.5' }}>
                  Your store is directly authenticated with CJ Dropshipping Developer Account <strong>CJ5963658</strong> (OpenID: <code>{cjConnection?.openId || 57878}</code>). Incoming Amazon storefront orders can be fulfilled on-demand through CJ&apos;s US domestic warehouses in Ontario, CA and New Jersey with blind neutral packaging and 2–3 day domestic delivery.
                </p>
              </div>

              <div className="calc-grid-clean two-col" style={{ marginTop: '16px' }}>
                <div className="dossier-stat-tile">
                  <span className="dossier-stat-label">CJ ACCOUNT USER</span>
                  <span className="dossier-stat-val tabular">CJ5963658</span>
                  <span className="dossier-stat-sub">Developer OpenID: {cjConnection?.openId || 57878}</span>
                </div>
                <div className="dossier-stat-tile">
                  <span className="dossier-stat-label">ACCESS TOKEN STATUS</span>
                  <span className="dossier-stat-val tabular" style={{ color: '#16A34A' }}>Active · 180-Day Token</span>
                  <span className="dossier-stat-sub">Expires April 20, 2027</span>
                </div>
                <div className="dossier-stat-tile">
                  <span className="dossier-stat-label">US DOMESTIC WAREHOUSES</span>
                  <span className="dossier-stat-val tabular">Ontario, CA &amp; NJ</span>
                  <span className="dossier-stat-sub">USPS Ground Advantage (2–3 Days)</span>
                </div>
                <div className="dossier-stat-tile">
                  <span className="dossier-stat-label">AMAZON BLIND PACKAGING</span>
                  <span className="dossier-stat-val tabular" style={{ color: '#D97706' }}>Enabled · Compliant</span>
                  <span className="dossier-stat-sub">Neutral shipping label · Zero supplier logo</span>
                </div>
              </div>

              <div style={{ marginTop: '20px', padding: '16px', background: '#FFFFFF', borderRadius: '8px', border: '1px solid var(--border-hairline)' }}>
                <span className="section-kicker" style={{ fontSize: '0.72rem', letterSpacing: '0.08em', color: '#64748B', fontWeight: 800 }}>
                  ORDER FULFILLMENT AUTOMATION LEVEL
                </span>
                <h4 style={{ margin: '4px 0 12px', fontSize: '0.96rem' }}>
                  Can the app fulfill Amazon orders without you?
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div
                    style={{
                      padding: '12px',
                      borderRadius: '6px',
                      border: `2px solid ${autoFulfillMode === 'zero_touch' ? '#10B981' : 'var(--border-hairline)'}`,
                      background: autoFulfillMode === 'zero_touch' ? 'rgba(16,185,129,0.06)' : '#FAFAFA',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleToggleMode('zero_touch')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <strong style={{ fontSize: '0.86rem', color: '#0F172A' }}>🟢 100% Zero-Touch (Auto-Pilot)</strong>
                      {autoFulfillMode === 'zero_touch' && <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#16A34A' }}>ACTIVE</span>}
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#475569', lineHeight: '1.4' }}>
                      <strong>Zero manual work:</strong> When an Amazon order comes in, the server automatically reads the buyer address, dispatches the order to CJ US warehouse via API, and uploads the USPS tracking number to Amazon Seller Central.
                    </p>
                  </div>

                  <div
                    style={{
                      padding: '12px',
                      borderRadius: '6px',
                      border: `2px solid ${autoFulfillMode === 'one_click_review' ? '#3B82F6' : 'var(--border-hairline)'}`,
                      background: autoFulfillMode === 'one_click_review' ? 'rgba(59,130,246,0.06)' : '#FAFAFA',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleToggleMode('one_click_review')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <strong style={{ fontSize: '0.86rem', color: '#0F172A' }}>🔵 1-Click Review (Semi-Auto)</strong>
                      {autoFulfillMode === 'one_click_review' && <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#2563EB' }}>ACTIVE</span>}
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#475569', lineHeight: '1.4' }}>
                      <strong>Safety review:</strong> The app prepares the order with locked net profit, customer address, and warehouse selection, and waits for your 1-click confirmation before spending funds.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '20px' }}>
                <label htmlFor="cj-api-key-input" style={{ fontWeight: 600, display: 'block', marginBottom: '6px', fontSize: '0.85rem' }}>
                  CJ Developer API Key
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    id="cj-api-key-input"
                    type="text"
                    value={cjKeyInput}
                    onChange={(e) => setCjKeyInput(e.target.value)}
                    className="tabular"
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '6px', border: '1px solid var(--border-hairline)', fontFamily: 'monospace', fontSize: '0.85rem' }}
                    placeholder="CJ5963658@api@..."
                  />
                  <button
                    type="button"
                    className="btn-outline"
                    disabled={cjSaving}
                    onClick={async () => {
                      if (!cjKeyInput.trim()) return;
                      setCjSaving(true);
                      try {
                        if (onSaveCjKey) {
                          await onSaveCjKey(cjKeyInput.trim());
                        }
                      } finally {
                        setCjSaving(false);
                      }
                    }}
                  >
                    {cjSaving ? 'Saving...' : 'Update Key'}
                  </button>
                </div>
              </div>

              <div style={{ marginTop: '20px', padding: '16px', background: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                  <div>
                    <strong style={{ fontSize: '0.9rem', color: '#0F172A' }}>Test Live CJ Inventory &amp; Order Bridge</strong>
                    <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748B' }}>
                      Sends a live ping to CJ API v2.0 endpoint for SKU <code>CJYS227337902BY</code> to verify query quota and warehouse connection.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-jet"
                    style={{ minHeight: '36px', padding: '0 16px', fontSize: '0.82rem' }}
                    disabled={cjTesting}
                    onClick={async () => {
                      setCjTesting(true);
                      setCjTestResult(null);
                      try {
                        const res = await fetch('/api/cj/lookup?sku=CJYS227337902BY');
                        const data = await res.json();
                        if (data.ok && data.found) {
                          setCjTestResult(`✓ Connected! Live Product PID ${data.product.pid} confirmed. Supplier: ${data.product.supplierName || 'Ningbo Zhongchen'} · Shipping: US Domestic`);
                        } else {
                          setCjTestResult(`✕ Response: ${JSON.stringify(data)}`);
                        }
                      } catch (e: unknown) {
                        setCjTestResult(`✕ Error: ${e instanceof Error ? e.message : 'Failed to reach API'}`);
                      } finally {
                        setCjTesting(false);
                      }
                    }}
                  >
                    {cjTesting ? 'Testing...' : '⚡ Test CJ API Bridge'}
                  </button>
                </div>
                {cjTestResult && (
                  <div style={{ marginTop: '12px', padding: '10px 14px', borderRadius: '6px', background: cjTestResult.startsWith('✓') ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${cjTestResult.startsWith('✓') ? '#86EFAC' : '#FCA5A5'}`, color: cjTestResult.startsWith('✓') ? '#166534' : '#991B1B', fontSize: '0.85rem' }}>
                    {cjTestResult}
                  </div>
                )}
              </div>

              <div style={{ marginTop: '16px', padding: '14px', background: '#FFFBEB', borderRadius: '8px', border: '1px solid #FDE68A', color: '#92400E', fontSize: '0.82rem', lineHeight: '1.5' }}>
                <strong>Why Amazon wasn&apos;t in CJ&apos;s &quot;Connect Store&quot; list:</strong><br />
                Amazon does not allow 3rd-party suppliers like CJ Dropshipping or AliExpress to publish public &quot;auto-sync apps&quot; inside Seller Central due to Amazon&apos;s strict Seller of Record policy. Instead, developers connect via the <strong>official CJ Dropshipping API v2.0</strong>—which is what we have connected for you here! Your orders can now be auto-routed to CJ US fulfillment with 1 click.
              </div>

              <div className="dialog-actions" style={{ marginTop: '20px' }}>
                <button type="button" className="btn-outline" onClick={onCloseAmazonConnect}>
                  Close
                </button>
                <button
                  type="button"
                  className="btn-jet"
                  onClick={() => {
                    window.open('https://cjdropshipping.com', '_blank', 'noopener,noreferrer');
                  }}
                >
                  Open CJ Dropshipping Portal ↗
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="spapi-guide-box">
                <strong>How to get your keys in 2 minutes:</strong>
                <ol>
                  <li>
                    Log in to <strong>sellercentral.amazon.com</strong> → <em>Settings → Account Info → Merchant Token</em> (copy your{' '}
                    <strong>Seller ID</strong>).
                  </li>
                  <li>
                    Go to <em>Apps &amp; Services → Develop Apps → Add New App Client</em> (select <strong>SP-API</strong>: Product Listing, Pricing &amp; FBA).
                  </li>
                  <li>
                    Copy your <strong>LWA Client ID</strong>, <strong>Client Secret</strong>, and click <em>Authorize</em> for your{' '}
                    <strong>Refresh Token</strong>—or leave blank to use <strong>Instant Sandbox Mode</strong> now.
                  </li>
                </ol>
              </div>

              <div className="calc-grid-clean two-col">
                <div className="field">
                  <label htmlFor="amz-seller-id">Amazon Seller ID (Merchant Token) *</label>
                  <input
                    type="text"
                    id="amz-seller-id"
                    required
                    value={sellerId}
                    onChange={(e) => setSellerId(e.target.value)}
                    placeholder="e.g., A3R7XJXJW7FKHB"
                    className="tabular"
                  />
                </div>
                <div className="field">
                  <label htmlFor="amz-marketplace-id">Amazon Marketplace</label>
                  <select id="amz-marketplace-id" value={marketplaceId} onChange={(e) => setMarketplaceId(e.target.value)}>
                    <option value="ATVPDKIKX0DER">Amazon.com US (ATVPDKIKX0DER)</option>
                    <option value="A2EUQ1WTGCTBG2">Amazon.ca Canada (A2EUQ1WTGCTBG2)</option>
                    <option value="A1F83G8C2ARO7P">Amazon.co.uk UK (A1F83G8C2ARO7P)</option>
                  </select>
                </div>
              </div>

              <div className="field">
                <label htmlFor="amz-lwa-client-id">LWA Client ID (Optional for Sandbox Mode)</label>
                <input
                  type="text"
                  id="amz-lwa-client-id"
                  value={lwaClientId}
                  onChange={(e) => setLwaClientId(e.target.value)}
                  placeholder="amzn1.application-oa2-client.xxxxxxxx"
                  className="tabular"
                />
              </div>

              <div className="calc-grid-clean two-col">
                <div className="field">
                  <label htmlFor="amz-lwa-client-secret">LWA Client Secret</label>
                  <input
                    type="password"
                    id="amz-lwa-client-secret"
                    value={lwaClientSecret}
                    onChange={(e) => setLwaClientSecret(e.target.value)}
                    placeholder="amzn1.oa2-cs.v1.xxxxxxxx"
                  />
                </div>
                <div className="field">
                  <label htmlFor="amz-lwa-refresh-token">LWA Refresh Token</label>
                  <input
                    type="password"
                    id="amz-lwa-refresh-token"
                    value={lwaRefreshToken}
                    onChange={(e) => setLwaRefreshToken(e.target.value)}
                    placeholder="Atzr|IwEBIA..."
                  />
                </div>
              </div>

              <div className="dialog-actions">
                <button type="button" className="btn-outline" id="cancel-amazon-connect-btn" onClick={onCloseAmazonConnect}>
                  Cancel
                </button>
                <button type="submit" className="btn-jet" id="save-amazon-connect-btn" disabled={amzSaving}>
                  {amzSaving ? 'Connecting...' : 'Save & Connect Amazon Store'}
                </button>
              </div>
            </>
          )}
        </form>
      </dialog>

      {/* MODAL 2: ADD NEW STOREFRONT */}
      <dialog
        ref={newStoreDialogRef}
        id="new-store-dialog"
        {...({ closedby: 'any' } as React.DialogHTMLAttributes<HTMLDialogElement>)}
        className="clean-dialog"
        aria-labelledby="new-store-dialog-title"
        onClose={onCloseNewStore}
      >
        <form
          id="new-store-form"
          className="dialog-body"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!storeName.trim()) {
              setStoreNameError(true);
              newStoreInputRef.current?.focus();
              return;
            }
            setStoreNameError(false);
            onCreateNewStore({
              name: storeName.trim(),
              channelKey: storeChannel,
              nicheFocus: storeNiche.trim()
            });
            setStoreName('');
            setStoreNiche('');
          }}
        >
          <div className="dialog-header">
            <h3 id="new-store-dialog-title">Launch New Storefront</h3>
            <button type="button" className="btn-close" id="close-new-store-btn" aria-label="Close dialog" onClick={onCloseNewStore}>
              <span aria-hidden="true">✕</span>
            </button>
          </div>
          <div className="field">
            <label htmlFor="new-store-name">
              Storefront Name <span aria-hidden="true">*</span>
            </label>
            <input
              ref={newStoreInputRef}
              type="text"
              id="new-store-name"
              required
              aria-invalid={storeNameError ? 'true' : undefined}
              aria-describedby="new-store-name-error"
              placeholder="e.g., Aurelius Pet Essentials"
              value={storeName}
              onChange={(e) => {
                setStoreName(e.target.value);
                if (storeNameError) setStoreNameError(false);
              }}
            />
            <p id="new-store-name-error" className="field-error" role="alert" hidden={!storeNameError}>
              Please enter a name for your new storefront.
            </p>
          </div>
          <div className="field">
            <label htmlFor="new-store-channel">
              Platform / Marketplace <span aria-hidden="true">*</span>
            </label>
            <select id="new-store-channel" value={storeChannel} onChange={(e) => setStoreChannel(e.target.value)}>
              <option value="amazon">Amazon US (FBA Prime — 15% Fee)</option>
              <option value="shopify">Shopify Direct-to-Consumer (2.9% Fee)</option>
              <option value="walmart">Walmart Marketplace WFS (15% Fee)</option>
              <option value="tiktok">TikTok Shop US (6% Fee)</option>
              <option value="ebay">eBay Brand Outlet (13.25% Fee)</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="new-store-niche">Primary Niche</label>
            <input
              type="text"
              id="new-store-niche"
              placeholder="e.g., Smart Home & Kitchen"
              value={storeNiche}
              onChange={(e) => setStoreNiche(e.target.value)}
            />
          </div>
          <div className="dialog-actions">
            <button type="button" className="btn-outline" id="cancel-new-store-btn" onClick={onCloseNewStore}>
              Cancel
            </button>
            <button type="submit" className="btn-jet">
              Create Storefront
            </button>
          </div>
        </form>
      </dialog>

      {/* MODAL 3: CLEAN PRODUCT GALLERY LIGHTBOX */}
      <dialog
        ref={galleryDialogRef}
        id="gallery-dialog"
        {...({ closedby: 'any' } as React.DialogHTMLAttributes<HTMLDialogElement>)}
        className="clean-dialog gallery-modal"
        aria-labelledby="dialog-title"
        onClose={onCloseLightbox}
      >
        <div className="dialog-body">
          <div className="dialog-header">
            <h3 id="dialog-title">{lightboxProduct?.title || 'Product Gallery'}</h3>
            <button type="button" className="btn-close" id="dialog-close-btn" aria-label="Close gallery" onClick={onCloseLightbox}>
              <span aria-hidden="true">✕</span>
            </button>
          </div>
          <div className="dialog-stage">
            <img
              id="dialog-main-img"
              src={lightboxProduct?.images[lightboxImgIdx] || '/images/fba-prod-01-1.jpg'}
              alt={`${lightboxProduct?.title || 'Product'} — Angle ${lightboxImgIdx + 1}`}
            />
          </div>
          <div id="dialog-thumbs" className="dialog-thumbs" role="group" aria-label="Product gallery thumbnails">
            {(lightboxProduct?.images || []).map((url, idx) => {
              const isSelected = idx === lightboxImgIdx;
              return (
                <button
                  key={url + idx}
                  type="button"
                  className={`thumb-btn ${isSelected ? 'active' : ''}`}
                  data-lightbox-img={url}
                  data-lightbox-index={idx + 1}
                  aria-label={`View angle ${idx + 1} of ${lightboxProduct?.images.length || 1}`}
                  aria-pressed={isSelected ? 'true' : 'false'}
                  onClick={() => setLightboxImgIdx(idx)}
                >
                  <img src={url} alt="" />
                </button>
              );
            })}
          </div>
        </div>
      </dialog>

      {/* MODAL 4: AMAZON SYNC CONFIRMATION DOSSIER */}
      <dialog
        ref={syncConfirmDialogRef}
        id="sync-confirmation-dialog"
        {...({ closedby: 'any' } as React.DialogHTMLAttributes<HTMLDialogElement>)}
        className="clean-dialog sync-confirm-modal"
        aria-labelledby="sync-confirm-title"
        onClose={onCloseSyncConfirm}
      >
        {renderSyncConfirmationBody()}
      </dialog>

      {/* MODAL 5: LISTING ISSUES ACROSS ALL STORES */}
      <dialog
        ref={issuesDialogRef}
        id="issues-dialog"
        data-product-id={issuesDialogState.productId || ''}
        {...({ closedby: 'any' } as React.DialogHTMLAttributes<HTMLDialogElement>)}
        className="clean-dialog issues-modal"
        aria-labelledby="issues-dialog-title"
        onClose={onCloseIssuesDialog}
      >
        <div className="dialog-body">
          <div className="dialog-header issues-header">
            <div style={{ flex: 1 }}>
              <span className="section-kicker">LISTING HEALTH · ALL STORES</span>
              <h3 id="issues-dialog-title">Listing Issues</h3>
              <p id="issues-dialog-product" className="issues-product-name line-clamp-2">
                {issuesProduct?.title || ''}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                type="button"
                id="issues-copy-fix-prompt-top-btn"
                onClick={handleCopyProductFixPrompt}
                style={{
                  background: copiedIssuePrompt ? '#15803D' : '#09090B',
                  color: '#FFFFFF',
                  border: '1px solid #D9B668',
                  borderRadius: 8,
                  padding: '8px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap'
                }}
                title="Copy a ready-to-paste Antigravity prompt that fixes all issues on this product"
              >
                <span>{copiedIssuePrompt ? '✓ Copied Fix Prompt!' : '📋 Copy Fix Prompt'}</span>
              </button>
              <button type="button" className="btn-close" id="issues-close-btn" aria-label="Close listing issues" onClick={onCloseIssuesDialog}>
                <span aria-hidden="true">✕</span>
              </button>
            </div>
          </div>

          <div id="issues-dialog-content" aria-live="polite">
            {issuesDialogState.loading ? (
              <p className="issues-loading">
                {issuesDialogState.liveChecking ? 'Checking Amazon Seller Central live…' : 'Loading issues…'}
              </p>
            ) : issuesDialogState.error ? (
              <p className="issues-store-empty error">Couldn&apos;t load issues: {issuesDialogState.error}</p>
            ) : issuesDialogState.report ? (
              <>
                <div className="issues-summary-strip">
                  <div className={`issues-summary-cell ${issuesDialogState.report.errorCount ? 'error' : ''}`}>
                    <span className="spec-micro-label">ERRORS</span>
                    <span className="issues-summary-val tabular">{issuesDialogState.report.errorCount}</span>
                  </div>
                  <div className={`issues-summary-cell ${issuesDialogState.report.warningCount ? 'warning' : ''}`}>
                    <span className="spec-micro-label">WARNINGS</span>
                    <span className="issues-summary-val tabular">{issuesDialogState.report.warningCount}</span>
                  </div>
                  <div className="issues-summary-cell">
                    <span className="spec-micro-label">STORES CHECKED</span>
                    <span className="issues-summary-val tabular">{issuesDialogState.report.stores.length}</span>
                  </div>
                </div>

                <div className="issues-store-list">
                  {issuesDialogState.report.stores.length ? (
                    issuesDialogState.report.stores.map((store) => {
                      const sorted = [...store.issues].sort((a, b) =>
                        a.severity === b.severity ? 0 : a.severity === 'error' ? -1 : 1
                      );
                      return (
                        <section key={store.storeId} className="issues-store" aria-label={`${store.storeName} issues`}>
                          <header className="issues-store-head">
                            <div>
                              <h4>{store.storeName}</h4>
                              <span className="issues-store-channel">{store.channel}</span>
                            </div>
                            {store.errorCount ? (
                              <span className="issue-count-pill error tabular">
                                {store.errorCount} error{store.errorCount === 1 ? '' : 's'}
                              </span>
                            ) : store.warningCount ? (
                              <span className="issue-count-pill warning tabular">
                                {store.warningCount} warning{store.warningCount === 1 ? '' : 's'}
                              </span>
                            ) : (
                              <span className="issue-count-pill clean">✓ Clear</span>
                            )}
                          </header>

                          {store.liveCheckError ? (
                            <p className="issues-store-note">Live check failed: {store.liveCheckError}</p>
                          ) : store.liveChecked ? (
                            <p className="issues-store-note">Checked live against Seller Central just now.</p>
                          ) : null}

                          {sorted.length ? (
                            <ul className="issue-list">
                              {sorted.map((i, idx) => (
                                <li key={`${i.code}:${idx}`} className={`issue-item ${i.severity}`}>
                                  <span className="issue-sev">{i.severity === 'error' ? 'Error' : 'Warning'}</span>
                                  <div className="issue-text">
                                    <p className="issue-msg">{i.message}</p>
                                    {i.hint && (
                                      <p className="issue-hint">
                                        <strong>How to fix:</strong> {i.hint}
                                      </p>
                                    )}
                                    <p className="issue-meta">
                                      <code>{i.code}</code>
                                      {i.field && <span>Field: {i.field}</span>}
                                      <span>{ISSUE_SOURCE_LABELS[i.source || ''] || 'Check'}</span>
                                    </p>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="issues-store-empty">No problems found for this store.</p>
                          )}
                        </section>
                      );
                    })
                  ) : (
                    <p className="issues-store-empty">This product isn&apos;t assigned to any store yet.</p>
                  )}
                </div>
              </>
            ) : null}
          </div>

          <div className="dialog-actions">
            <a
              href={issuesAmzUrl}
              id="issues-view-amz-btn"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
              style={{ marginRight: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
              title={
                issuesProduct
                  ? `View ${issuesProduct.title} live on Amazon.com (ASIN: ${issuesAsin || '—'} · BackdropsForLess) ↗`
                  : undefined
              }
            >
              <span>🛒 View on Amazon</span>
              <span aria-hidden="true">↗</span>
            </a>
            <button
              type="button"
              id="issues-copy-fix-prompt-btn"
              className="btn-outline"
              style={{
                borderColor: '#9E782F',
                background: copiedIssuePrompt ? '#15803D' : '#FAF6EC',
                color: copiedIssuePrompt ? '#FFFFFF' : '#09090B',
                fontWeight: 700
              }}
              onClick={handleCopyProductFixPrompt}
            >
              {copiedIssuePrompt ? '✓ Copied Fix Prompt!' : '📋 Copy Fix Prompt'}
            </button>
            <button
              type="button"
              className="btn-outline"
              id="issues-recheck-btn"
              disabled={issuesDialogState.loading}
              onClick={() => {
                if (issuesDialogState.productId) {
                  onRecheckIssuesLive(issuesDialogState.productId);
                }
              }}
            >
              Re-check Amazon live
            </button>
            <button type="button" className="btn-jet" id="issues-done-btn" onClick={onCloseIssuesDialog}>
              Done
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
};
